import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getFullAuthUser } from 'store/selectors/auth';
import { getIsMenuOpen } from 'store/selectors/currentCommunity';
import { Community } from 'types';
import {
  canUserEditCommunity,
  getCommonCommunityLinks,
  getCommunityLinks,
} from 'utils/common';

import Content from './Content';
import Header from './Header';
import { Layout, StyledWrapper } from './styled';

interface CommunityLandingPageProps {
  community: Community;
  isUserInCommunity: boolean;
  sendRequest: () => void;
}

const CommunityLandingPage: FC<CommunityLandingPageProps> = ({
  community,
  isUserInCommunity,
  sendRequest = () => {},
}) => {
  const { t } = useTranslation();
  const currentUser = useSelector(getFullAuthUser);
  const isMenuOpen = useSelector(getIsMenuOpen);

  const { name, cover, logo, description, invites, usersCount, users } =
    community;
  const socialLinks = getCommunityLinks(community);

  // Translate common links labels based on current language
  const commonLinks = useMemo(() => {
    const links = getCommonCommunityLinks(community);
    return links.map((link) => ({
      ...link,
      label: link.label === 'website' ? t('community.website') : link.label,
    }));
  }, [community, t]);

  const hasAdminPermissions = canUserEditCommunity(currentUser, community);

  const inviteSent = useMemo(() => {
    return invites ? invites.some((el) => el.userId === currentUser.id) : false;
  }, [invites, currentUser.id]);

  return (
    <StyledWrapper
      isUserInCommunity={isUserInCommunity}
      isMenuOpen={isMenuOpen}
    >
      <Layout>
        <Header
          title={name}
          cover={cover}
          logo={logo}
          isUserInCommunity={isUserInCommunity}
          isMenuOpen={isMenuOpen}
          hasAdminPermissions={hasAdminPermissions}
          usersCount={users?.length ?? usersCount}
        />
        <Content
          buttonDisabled={inviteSent}
          description={description}
          links={commonLinks}
          isUserInCommunity={isUserInCommunity}
          sendRequest={sendRequest}
          socialLinks={socialLinks}
          isMenuOpen={isMenuOpen}
        />
      </Layout>
    </StyledWrapper>
  );
};

export default CommunityLandingPage;
